import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import AddProperty from "@/pages/AddProperty";
import EditProperty from "@/pages/EditProperty";
import Cashflow from "@/pages/Cashflow";
import Forecasting from "@/pages/Forecasting";
import Recommendations from "@/pages/Recommendations";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Admin from "@/pages/Admin";
import Partners from "@/pages/Partners";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Settings from "@/pages/Settings";
import PropertyAnalyser from "@/pages/PropertyAnalyser";
import PropertyResearcher from "@/pages/PropertyResearcher";
import SuburbResearcher from "@/pages/SuburbResearcher";
import { UsageLimitDialog } from "@/components/UsageLimitDialog";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import PartnerDashboard from "@/pages/PartnerDashboard";
import MyPartners from "@/pages/MyPartners";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // This must be checked synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="properties" element={<Properties />} />
        <Route path="properties/new" element={<AddProperty />} />
        <Route path="properties/:id" element={<PropertyDetail />} />
        <Route path="properties/:id/edit" element={<EditProperty />} />
        <Route path="cashflow" element={<Cashflow />} />
        <Route path="forecasting" element={<Forecasting />} />
        <Route path="recommendations" element={<Recommendations />} />
        <Route path="property-analyser" element={<PropertyAnalyser />} />
        <Route path="property-researcher" element={<PropertyResearcher />} />
        <Route path="suburb-researcher" element={<SuburbResearcher />} />
        <Route path="settings" element={<Settings />} />
        <Route path="partner-dashboard" element={<PartnerDashboard />} />
        <Route path="my-partners" element={<MyPartners />} />
        <Route path="admin" element={<Admin />} />
        <Route path="admin/partners" element={<Partners />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className="min-h-screen bg-[#F8F9FA]">
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <UsageLimitDialog />
      </div>
    </APIProvider>
  );
}

export default App;
